import React from "react";
import { connect } from "react-redux";

import User from "../core/types/core/User";
import {
    addAllUser,
    deleteUsers,
    importUsersFromAzr,
    importUsersFromTD,
    initialiseAllUserDetails,
    initialiseUserPasswordDetails,
    sendActivations,
    updateAllUser,
} from "../core/state/core/User";
import Grid from "../core/components/grid/Grid";
import GridColumn, { GridColumnType } from "../core/components/grid/GridColumn";
import ToolbarButton from "../core/components/toolbar/ToolbarButton";
import MainToolbarContents from "../core/components/toolbar/MainToolbarContents";
import Dialog from "../core/components/dialog/Dialog";
import DialogContent from "../core/components/dialog/DialogContent";
import DialogFooter from "../core/components/dialog/DialogFooter";
import DialogCancel from "../core/components/dialog/DialogCancel";
import Wizard from "../core/components/wizard/Wizard";
import WizardStep from "../core/components/wizard/WizardStep";
import DialogOK from "../core/components/dialog/DialogOK";
import Spacer from "../core/components/containers/Spacer";
import { TitleType } from "../core/types/core/Person";
import Utilities from "../core/foundation/Utility";
import InputForm from "../core/components/inputform/InputForm";
import RenderOrientation from "../core/types/enums/RenderOrientation";
import InputFormField from "../core/components/inputform/InputFormField";
import InputFieldTypes from "../core/types/enums/ImportFieldTypes";
import Panel from "../core/components/panels/Panel";
import { FlexDirection, Side, Size } from "../core/types/system/Layout";
import Toolbar from "../core/components/toolbar/Toolbar";
import RecordsPanel from "../core/components/panels/RecordsPanel";
import EmailAddress from "../core/types/core/EmailAddress";
import EmailAddressEditor from "../core/components/emailaddresses/EmailAddressEditor";
import TTSDebouncer from "../core/foundation/TTSDebouncer";
import Datastore from "../core/communication/Datastore";
import { setPageLoading } from "../core/state/system/UIConfiguration";
import PhoneNumber from "../core/types/core/PhoneNumber";
import Address from "../core/types/core/Address";
import PasswordConfig from "../core/types/passwords/PasswordConfig";
import PasswordEditor from "../core/components/passwords/PasswordEditor";
import PasswordChange from "../core/types/passwords/PasswordChange";
import GeneralContainer from "../core/components/containers/GeneralContainer";
import Checkbox from "../core/components/inputform/Checkbox";
import IconStyles from "../core/types/enums/IconStyles";
import PhoneNumberEditor from "../core/components/phoneNumbers/PhoneNumberEditor";
import NumberTypes from "../core/types/enums/NumberTypes";
import AddressEditor from "../core/components/addresses/AddressEditor";
import InputFormSubform from "../core/components/inputform/InputFormSubform";
import InputFormMandatory from "../core/components/inputform/InputFormMandatory";
import { initialiseUserRolesDetails } from "../core/state/core/UserRoles";
import UserRole from "../core/types/core/UserRole";
import { AppDispatch, IAppState } from "../core/state/Store";
import School from "../core/types/core/School";
import { initialiseSchoolDetails } from "../core/state/core/School";
import { AlertTypes } from "../core/types/system/AlertEntry";
import { unwrappedDispatch } from "../core/foundation/Dispatching";
import { addAlert } from "../core/state/system/Alerts";
import SelectList from "../core/components/inputform/SelectList";
import Import from "../core/components/import/Import";
import {
    SpreadsheetConfiguration,
    SpreadsheetTableDefinition,
} from "../core/types/data/SpreadsheetConfiguration";
import { DataSet } from "../core/types/data/DataSet";
import FileExplorer from "../core/components/fileExplorer/FileExplorer";
import File from "../core/types/files/File";
import { FileExplorerTypes } from "../core/types/system/FileExplorer";
import Info from "../core/components/info/Info";
import { User_ImportOptions } from "../core/types/core/User_ImportOptions";
import {
    ImportLog,
    SkippedRecordCount,
    UpsertCount,
} from "../core/types/core/ImportLog";
import { DataTable } from "../core/types/data/DataTable";
import { UserGroup } from "../core/types/core/UserGroup";
import WebRequest from "../core/communication/WebRequest";

import template from "../resources/User_Import_Template.xlsx";

import "./Users.scss";
import AzImportSettings from "../core/types/core/AzImportSettings";
import AzADTenantInfo from "../core/types/core/AzADTenantInfo";
import AzAuthResponse from "../core/types/core/AzAuthResponse";
import AzImportMapping from "../core/types/core/AzImportMapping";

interface InputUserRole {
    id: number;
    name: string;
    details: string;
    checked: boolean;
    available: boolean;
}

interface InputUserSchool {
    id: number;
    name: string;
    location: string;
    checked: boolean;
}

interface RouteUsersProps {
    users: User[];
    userRoles: UserRole[];
    schools: School[];
    customApps: boolean;
    passwordConfig: number;

    setPageLoading: (loading: boolean) => void;
    initialiseAllUserDetails: () => Promise<void>;
    initialiseUserPasswordDetails: () => Promise<void>;
    initialiseUserRolesDetails: () => Promise<UserRole[]>;
    initialiseSchoolDetails: () => Promise<void>;
    updateAllUser: (details: User[]) => Promise<void>;
    deleteUsers: (users: User[]) => Promise<void>;
}

interface RouteUsersState {
    bulk: boolean;

    editOpen: boolean;
    editLoad: boolean;
    editStep: number;

    schoolsOpen: boolean;
    schoolsLoad: boolean;

    rolesOpen: boolean;
    rolesLoad: boolean;

    importOpen: boolean;
    importLoad: boolean;
    importType: string;
    importStep: number;
    importWidth: number;
    importValid: boolean;

    selection: User | null;
    selections: User[];
    userRoles: InputUserRole[];
    userSchools: InputUserSchool[];

    editing: User | null;
    showPasswordEdit: boolean;
    autogeneratePassword: boolean;
    autogenerateSelf: boolean;
    passwordChanged: string;
    canSubmitPassword: boolean;

    password: PasswordConfig;
    usernameValid: string;
    usernameValidDebouncer: TTSDebouncer;
}

class RouteUsers extends React.Component<RouteUsersProps, RouteUsersState> {
    state: RouteUsersState = {
        bulk: false,

        editOpen: false,
        editLoad: false,
        editStep: 0,

        schoolsOpen: false,
        schoolsLoad: false,

        rolesOpen: false,
        rolesLoad: false,

        importOpen: false,
        importLoad: false,
        importType: undefined,
        importStep: 0,
        importWidth: 400,
        importValid: false,

        selection: null,
        selections: [],
        userRoles: [],
        userSchools: [],

        editing: null,
        showPasswordEdit: false,
        autogeneratePassword: false,
        autogenerateSelf: false,
        passwordChanged: "",
        canSubmitPassword: false,

        password: {} as PasswordConfig,
        usernameValid: "",
        usernameValidDebouncer: new TTSDebouncer(500),
    };

    static getDerivedStateFromProps(
        props: RouteUsersProps,
        state: RouteUsersState,
    ) {
        return {
            importWidth:
                state.importStep === 0
                    ? state.importType === "csv"
                        ? 1000
                        : state.importType === "tdf"
                        ? 580
                        : state.importType === "azr"
                        ? 580
                        : 580
                    : 580,
        };
    }

    constructor(props: RouteUsersProps) {
        super(props);

        const temp = this.props.passwordConfig || 180031;
        const chars = Math.floor(temp / 10000);
        const raw = temp - chars * 10000;
        this.state.password = {
            passwordMinLength: chars,
            passwordIncludeUpperLetter: (Math.pow(2, 0) & raw) > 0,
            passwordIncludeLowerLetter: (Math.pow(2, 1) & raw) > 0,
            passwordIncludeNumber: (Math.pow(2, 2) & raw) > 0,
            passwordIncludeSpecial: (Math.pow(2, 3) & raw) > 0,
            passwordExcludeNames: (Math.pow(2, 4) & raw) > 0,
        };
    }

    componentDidMount() {
        try {
            this.props.setPageLoading(true);

            this.props.initialiseAllUserDetails();
            this.props.initialiseUserPasswordDetails();
            this.props.initialiseUserRolesDetails();
            this.props.initialiseSchoolDetails();
        } finally {
            this.props.setPageLoading(false);
        }
    }

    add() {
        this.setState({
            editing: new User({
                id: 0,
                emails: [
                    {
                        id: 0,
                        primary: true,
                        unsubscribed: false,
                        address: "",
                    } as EmailAddress,
                ],
                numbers: [],
                addresses: [],
                active: true,
            }),
            editOpen: true,
            editLoad: false,
            editStep: 0,
        });
    }

    bulk() {}

    async edit() {
        if (!this.state.selection) return;

        try {
            this.props.setPageLoading(true);

            const user = new User(
                await Datastore.getType<User>(
                    `api/Users/${this.state.selection.id}`,
                ),
            );

            const chars = Math.floor(this.props.passwordConfig / 10000);
            const raw = this.props.passwordConfig - chars * 10000;

            this.setState({
                usernameValid: "check",
                password: {
                    passwordMinLength: chars,
                    passwordIncludeUpperLetter: (Math.pow(2, 0) & raw) > 0,
                    passwordIncludeLowerLetter: (Math.pow(2, 1) & raw) > 0,
                    passwordIncludeNumber: (Math.pow(2, 2) & raw) > 0,
                    passwordIncludeSpecial: (Math.pow(2, 3) & raw) > 0,
                    passwordExcludeNames: (Math.pow(2, 4) & raw) > 0,
                },
                editing: user,
                editOpen: true,
                editLoad: false,
                editStep: 0,
            });
        } finally {
            this.props.setPageLoading(false);
        }
    }

    addEmail() {
        this.setState(prev => ({
            editing: {
                ...prev.editing,
                emails: prev.editing.emails.concat([
                    {
                        id: 0,
                        primary: false,
                        unsubscribed: false,
                        address: "",
                    } as EmailAddress,
                ]),
            } as User,
        }));
    }
    updateEmail(address: EmailAddress, updated: EmailAddress) {
        if (!this.state.editing) return;

        let updatedCollection = [...this.state.editing.emails];
        let index = this.state.editing.emails.indexOf(address);
        if (updated.primary)
            updatedCollection.forEach(item => (item.primary = false));
        updatedCollection.splice(index, 1, updated);
        this.setState(() => ({
            editing: {
                ...this.state.editing,
                emails: updatedCollection,
            } as User,
        }));

        if (updated.primary) {
            this.setState(() => ({ usernameValid: "autorenew" }));

            this.state.usernameValidDebouncer.call(() => {
                this.validateUsername(updated.address);
            });
        }
    }
    async validateUsername(username: string) {
        if (!this.state.editing) return;

        let valid = true;
        if (username.length < 1) valid = false;
        else if (!Utilities.validEmailAddress(username)) valid = false;
        else if (
            await Datastore.getType<boolean>(
                `api/Users/${
                    this.state.editing.id ?? 0
                }/Username/${encodeURIComponent(username)}/InUse`,
            )
        )
            valid = false;

        this.setState(() => ({
            usernameValid: !valid ? "error" : "check",
        }));
    }
    deleteEmailAddress(address: EmailAddress) {
        let index = this.state.editing.emails.indexOf(address);
        let updated = [...this.state.editing.emails];
        updated.splice(index, 1);
        this.setState(() => ({
            editing: {
                ...this.state.editing,
                emails: updated,
            } as User,
        }));
    }

    editPassword() {
        this.setState(() => ({
            showPasswordEdit: !this.state.showPasswordEdit,
        }));
    }

    addAddress() {
        this.setState(() => ({
            editing: {
                ...this.state.editing,
                addresses: this.state.editing.addresses.concat([
                    {
                        id: 0,
                        line1: "",
                        line2: "",
                        city: "",
                        state: -1,
                        country: -1,
                        postcode: "",
                    } as Address,
                ]),
            } as User,
        }));
    }
    updateEditAddress(address: Address, updated: Address) {
        let index = this.state.editing.addresses.indexOf(address);
        let updatedCollection = [...this.state.editing.addresses];
        updatedCollection.splice(index, 1, updated);
        this.setState(() => ({
            editing: {
                ...this.state.editing,
                addresses: updatedCollection,
            } as User,
        }));
    }
    deleteAddress(address: Address) {
        let index = this.state.editing.addresses.indexOf(address);
        let updated = [...this.state.editing.addresses];
        updated.splice(index, 1);
        this.setState(() => ({
            editing: {
                ...this.state.editing,
                addresses: updated,
            } as User,
        }));
    }

    addNumber() {
        this.setState(() => ({
            editing: {
                ...this.state.editing,
                numbers: this.state.editing.numbers.concat([
                    {
                        id: 0,
                        type: NumberTypes.Phone,
                        phoneNumber: "",
                    } as PhoneNumber,
                ]),
            } as User,
        }));
    }
    updateEditNumber(number: PhoneNumber, updated: PhoneNumber) {
        let index = this.state.editing.numbers.indexOf(number);
        let updatedCollection = [...this.state.editing.numbers];
        updatedCollection.splice(index, 1, updated);
        this.setState(() => ({
            editing: {
                ...this.state.editing,
                numbers: updatedCollection,
            } as User,
        }));
    }
    deleteNumber(number: PhoneNumber) {
        let index = this.state.editing.numbers.indexOf(number);
        let updated = [...this.state.editing.numbers];
        updated.splice(index, 1);
        this.setState(() => ({
            editing: {
                ...this.state.editing,
                numbers: updated,
            } as User,
        }));
    }

    cancelEdit() {
        this.setState({ editing: null, editOpen: false });
    }

    prevEdit() {
        this.setState({ editStep: this.state.editStep - 1 });
    }

    async nextEdit() {
        try {
            let step = this.state.editStep + 1;
            this.setState({ editLoad: true });

            switch (step) {
                case 1:
                    const transmitDetails = new User(
                        structuredClone(this.state.editing) as User,
                    );
                    if (this.state.showPasswordEdit) {
                        if (this.state.autogeneratePassword) {
                            delete transmitDetails.password;
                            transmitDetails.generatePassword =
                                this.state.autogeneratePassword;
                            transmitDetails.emailPassword =
                                this.state.autogenerateSelf;
                        } else {
                            transmitDetails.password =
                                this.state.passwordChanged;
                            transmitDetails.generatePassword = false;
                            transmitDetails.emailPassword = false;
                        }
                    }

                    transmitDetails.email = this.state.editing.emails.find(
                        e => e.primary,
                    )?.address;
                    transmitDetails.username = transmitDetails.email;

                    await this.props.updateAllUser([transmitDetails]);

                    if (this.state.editing.id > 0) {
                        this.setState({
                            editOpen: false,
                            editing: null,
                        });
                    } else {
                        this.setState({
                            userRoles: this.props.userRoles
                                .filter(ur => ur.licenced)
                                .map(ur => ({
                                    id: ur.id,
                                    name: ur.name,
                                    details: ur.description,
                                    checked: transmitDetails.roles.includes(
                                        ur.name,
                                    ),
                                    available: ur.availableCount > 0,
                                })),
                        });
                    }
                    break;
                case 2:
                    const transmitRoles = new User(
                        structuredClone(this.state.editing) as User,
                    );

                    transmitRoles.roles = this.state.userRoles
                        .filter(ur => ur.checked)
                        .map(ur => ur.name)
                        .concat(["Users"]);

                    await this.props.updateAllUser([transmitRoles]);

                    this.setState({ editOpen: false, editing: null });
                    break;
            }

            this.setState({ editStep: step });
        } finally {
            this.setState({ editLoad: false });
        }
    }

    async delete() {
        const multiUsers = this.state.selections.filter(
            item => item.schools.length > 1,
        );

        const multiUserMessage =
            multiUsers.length === 0
                ? ""
                : `<br/><div style="font-weight: bold;">${multiUsers
                      .map(item => item.username)
                      .join(",")} ${
                      multiUsers.length === 1 ? "has" : "have"
                  } access to multiple school sites. Proceeding will retain their account and only remove them from this school.</div>`;

        const result = await addAlert({
            type: AlertTypes.Confirm,
            icon: "delete_forever",
            styleColor: "red",
            title: "Delete Users",
            text: `Are you sure you want to delete the listed users?<br/><ul class="indented chevron"><li>${this.state.selections
                .map(item => item.username)
                .join("</li><li>")}</li></ul>${multiUserMessage}`,
        });

        if (result.response) {
            try {
                this.props.setPageLoading(true);

                await this.props.deleteUsers(this.state.selections);

                this.setState({ selection: null, selections: [] });
            } finally {
                this.props.setPageLoading(false);
            }
        }
    }

    schools() {
        this.setState({
            userSchools: this.props.schools.map(school => ({
                id: school.id,
                name: school.name,
                location: school.address.city,
                checked:
                    this.state.selections.length > 1
                        ? false
                        : this.state.selection.schools
                        ? this.state.selection.schools.includes(school.id)
                        : false,
            })),
            schoolsOpen: true,
        });
    }

    schoolsCancel() {
        this.setState({ schoolsOpen: false, userSchools: [] });
    }

    schoolChanged(school: InputUserSchool, update: Partial<InputUserSchool>) {
        this.setState({
            userSchools: this.state.userSchools.map(us =>
                us.id === school.id ? { ...us, ...update } : us,
            ),
        });
    }

    async schoolsSave() {
        try {
            this.setState({ schoolsLoad: true });
            const schoolIds = this.state.userSchools
                .filter(us => us.checked)
                .map(us => us.id);
            const transmitUsers = this.state.selections.map(selection => {
                const transmit = new User(structuredClone(selection) as User);
                transmit.schools = schoolIds;
                return transmit;
            });

            await this.props.updateAllUser(transmitUsers);

            this.setState({ schoolsOpen: false, userSchools: [] });
        } finally {
            this.setState({ schoolsLoad: false });
        }
    }

    roles() {
        this.setState({
            userRoles: this.props.userRoles
                .filter(role => role.licenced)
                .map(role => ({
                    id: role.id,
                    name: role.name,
                    details: role.description,
                    checked:
                        this.state.selections.length > 1
                            ? false
                            : this.state.selection.roles
                            ? this.state.selection.roles.includes(role.name)
                            : false,
                    available:
                        this.state.selections.length > 1
                            ? true
                            : role.availableCount > 0 ||
                              this.state.selection.roles.includes(role.name),
                })),
            rolesOpen: true,
        });
    }

    rolesCancel() {
        this.setState({ rolesOpen: false, userRoles: [] });
    }

    rolesChanged(role: InputUserRole, update: Partial<InputUserRole>) {
        this.setState({
            userRoles: this.state.userRoles.map(ur =>
                ur.id === role.id ? { ...ur, ...update } : ur,
            ),
        });
    }

    async rolesSave() {
        try {
            this.setState({ rolesLoad: true });
            const roleNames = this.state.userRoles
                .filter(ur => ur.checked)
                .map(ur => ur.name)
                .concat(["Users"]);
            const transmitUsers = this.state.selections.map(selection => {
                const transmit = new User(structuredClone(selection) as User);
                transmit.roles = roleNames;
                return transmit;
            });

            await this.props.updateAllUser(transmitUsers);

            this.setState({ rolesOpen: false, userRoles: [] });
        } finally {
            this.setState({ rolesLoad: false });
        }
    }

    apps() {}

    import() {
        this.setState({
            importOpen: true,
            importStep: 0,
            importType: undefined,
        });
    }

    importSetType(type: string) {
        this.setState({ importType: type, importStep: 0 });
    }

    importSetStep(step: number) {
        this.setState({ importStep: step });
    }

    importNextStep() {
        this.setState({ importStep: this.state.importStep + 1 });
    }

    importLastStep() {
        this.setState({ importStep: this.state.importStep - 1 });
    }

    importProceedChanged(valid: boolean) {
        this.setState({ importValid: valid });
    }

    importCancel() {
        this.setState({ importOpen: false });
    }

    async activation() {
        this.setState({ editLoad: true });

        await sendActivations(this.state.selections.map(item => item.id));

        addAlert({
            icon: "mode_off_on",
            title: "Send Activations",
            text: "Activation emails sent",
        });

        this.setState({ editLoad: false });
    }

    async activations() {
        const result = await addAlert({
            type: AlertTypes.Confirm,
            icon: "mode_off_on",
            title: "Send Activations",
            text: `Are you sure you want to send activation emails to the listed users?<br/><ul><li>${this.state.selections
                .map(item => item.username)
                .join("</li><li>")}</li></ul>`,
            okText: "Yes",
            cancelText: "No",
        });

        if (result.response) {
            this.props.setPageLoading(true);

            await sendActivations(this.state.selections.map(item => item.id));

            addAlert({
                icon: "mode_off_on",
                title: "Send Activations",
                text: "Activation emails sent",
            });

            this.props.setPageLoading(false);
        }
    }

    render() {
        return (
            <section>
                <MainToolbarContents>
                    <ToolbarButton
                        icon="add"
                        text="Add"
                        onClick={this.add.bind(this)}
                    />
                    <ToolbarButton
                        icon="list"
                        text="Bulk"
                        onClick={this.bulk.bind(this)}
                    />
                    <ToolbarButton
                        icon="edit"
                        text="Edit"
                        data-tip={`Edit the selected User${
                            this.state.bulk ? "s" : ""
                        }`}
                        disabled={this.state.selections.length === 0}
                        onClick={this.edit.bind(this)}
                    />
                    {!this.state.bulk && (
                        <>
                            <ToolbarButton
                                icon="location_city"
                                text="Schools"
                                data-tip="Edit the selected Users School Access"
                                disabled={this.state.selections.length === 0}
                                onClick={this.schools.bind(this)}
                            />
                            <ToolbarButton
                                icon="assignment_ind"
                                text="Roles"
                                data-tip="Edit the selected users' roles"
                                disabled={this.state.selections.length === 0}
                                onClick={this.roles.bind(this)}
                            />
                            {this.props.customApps && (
                                <ToolbarButton
                                    icon="vpn_key"
                                    text="Apps"
                                    data-tip="Edit the selected users' Access to App Integrations"
                                    disabled={
                                        this.state.selections.length === 0
                                    }
                                    onClick={this.apps.bind(this)}
                                />
                            )}
                            <ToolbarButton
                                icon="delete"
                                text="Delete"
                                data-tip="Delete the selected users"
                                disabled={this.state.selections.length === 0}
                                onClick={this.delete.bind(this)}
                            />
                            <ToolbarButton
                                icon="system_update_alt"
                                text="Import"
                                data-tip="Import user accounts"
                                onClick={this.import.bind(this)}
                            />
                            <ToolbarButton
                                icon="mode_off_on"
                                text="Activations"
                                data-tip="Send activation emails to the selected users"
                                disabled={this.state.selections.length === 0}
                                onClick={this.activations.bind(this)}
                            />
                        </>
                    )}
                </MainToolbarContents>

                <Grid<User>
                    value={this.props.users}
                    keyField="id"
                    window={true}
                    defaultSortField="username"
                    selectionChanged={(selection, selections) =>
                        this.setState({ selection, selections })
                    }
                    cellDoubleClicked={this.edit.bind(this)}>
                    <GridColumn
                        header="No"
                        value="[RowNo]"
                        selectable={true}
                        width="40px"
                    />
                    <GridColumn
                        header="Type"
                        field="type"
                        selectable={true}
                        filterable={true}
                        type={GridColumnType.Dropdown}
                        options={[
                            { value: "User", text: "User" },
                            { value: "Teacher", text: "Teacher" },
                            { value: "Teacher", text: "Teacher" },
                        ]}
                    />
                    <GridColumn
                        header="Username"
                        field="username"
                        selectable={true}
                        filterable={true}
                    />
                    <GridColumn
                        header="First Name"
                        field="firstName"
                        selectable={true}
                        editable={true}
                        filterable={true}
                    />
                    <GridColumn
                        header="Family Name"
                        field="lastName"
                        selectable={true}
                        editable={true}
                        filterable={true}
                    />
                    <GridColumn
                        header="Code"
                        field="code"
                        selectable={true}
                        filterable={true}
                    />
                    <GridColumn
                        header="Online"
                        field="active"
                        selectable={true}
                        editable={true}
                        filterable={true}
                        type={GridColumnType.Checkbox}
                    />
                </Grid>

                <Dialog
                    showDialog={this.state.editOpen}
                    showLoading={this.state.editLoad}
                    dialogIcon="person"
                    dialogTitle="User Details"
                    canResize={false}
                    width={620}>
                    <DialogContent>
                        <Wizard
                            frameless={true}
                            height="80vh"
                            step={this.state.editStep}
                            stepChanged={step =>
                                step !== this.state.editStep &&
                                this.setState({ editStep: step })
                            }>
                            <WizardStep step={0}>
                                <InputForm
                                    binding={this.state.editing}
                                    fullWidth={true}
                                    display={RenderOrientation.Horizontal}
                                    fieldDisplay={RenderOrientation.Vertical}
                                    onChange={value =>
                                        this.setState(() => ({
                                            editing: value,
                                        }))
                                    }>
                                    <InputFormField
                                        label="Title"
                                        field="title"
                                        type={InputFieldTypes.Select}
                                        options={Utilities.enumToOptions(
                                            TitleType,
                                        )}
                                        weight={60}
                                    />
                                    <InputFormField
                                        label="First Name"
                                        field="firstName"
                                        weight={100}
                                        mandatory={true}
                                    />
                                    <InputFormField
                                        label="Middle Name"
                                        field="middleName"
                                        weight={100}
                                    />
                                    <InputFormField
                                        label="Family Name"
                                        field="lastName"
                                        weight={100}
                                        mandatory={true}
                                    />
                                </InputForm>

                                <RecordsPanel<EmailAddress>
                                    typeName="Email Address"
                                    additionalMessage="You must provide at least one email address to be used for login."
                                    clearanceSide={Side.Top}
                                    inlineRemove={true}
                                    noRecordTitle={true}
                                    records={this.state.editing?.emails ?? []}
                                    canRemove={record => !record.primary}
                                    editor={record => (
                                        <EmailAddressEditor
                                            value={record}
                                            valid={this.state.usernameValid}
                                            onChange={updated =>
                                                this.updateEmail(
                                                    record,
                                                    updated,
                                                )
                                            }
                                        />
                                    )}
                                    onAdd={this.addEmail.bind(this)}
                                    onDelete={record =>
                                        this.deleteEmailAddress(record)
                                    }
                                />

                                <Panel
                                    title="Password"
                                    bufferSide={Side.All}
                                    clearanceSide={Side.Top}>
                                    <Toolbar
                                        direction={RenderOrientation.RightLeft}>
                                        <ToolbarButton
                                            icon={
                                                this.state.showPasswordEdit
                                                    ? "block"
                                                    : "edit"
                                            }
                                            iconStyle={
                                                this.state.showPasswordEdit
                                                    ? IconStyles.Red
                                                    : IconStyles.Normal
                                            }
                                            onClick={this.editPassword.bind(
                                                this,
                                            )}
                                        />
                                    </Toolbar>

                                    {this.state.showPasswordEdit && (
                                        <GeneralContainer
                                            bufferSide={Side.All}
                                            bufferSize={Size.Small}>
                                            <Checkbox
                                                label="Automatically create a password"
                                                tooltip="Generates a new random password which complies with your schools security requirements and emails it to the user"
                                                value={
                                                    this.state
                                                        .autogeneratePassword
                                                }
                                                onChange={value =>
                                                    this.setState(() => ({
                                                        autogeneratePassword:
                                                            value,
                                                    }))
                                                }
                                            />

                                            {this.state.autogeneratePassword ? (
                                                <Checkbox
                                                    label="Send me a copy of the new password"
                                                    tooltip="Sends a copy of the new password email to your email address"
                                                    value={
                                                        this.state
                                                            .autogenerateSelf
                                                    }
                                                    onChange={value =>
                                                        this.setState(() => ({
                                                            autogenerateSelf:
                                                                value,
                                                        }))
                                                    }
                                                />
                                            ) : (
                                                <PasswordEditor
                                                    slim={true}
                                                    password={
                                                        this.state.password
                                                    }
                                                    passwordsChanged={(
                                                        changes: PasswordChange,
                                                    ) =>
                                                        this.setState(() => ({
                                                            passwordChanged:
                                                                changes.updatedPassword,
                                                            canSubmitPassword:
                                                                changes.valid,
                                                        }))
                                                    }
                                                />
                                            )}
                                        </GeneralContainer>
                                    )}
                                </Panel>

                                <RecordsPanel<PhoneNumber>
                                    typeName="Phone Number"
                                    clearanceSide={Side.Top}
                                    inlineRemove={true}
                                    noRecordTitle={true}
                                    records={this.state.editing?.numbers ?? []}
                                    editor={record => (
                                        <PhoneNumberEditor
                                            value={record}
                                            onChange={updated =>
                                                this.updateEditNumber(
                                                    record,
                                                    updated,
                                                )
                                            }
                                        />
                                    )}
                                    onAdd={this.addNumber.bind(this)}
                                    onDelete={record =>
                                        this.deleteNumber(record)
                                    }
                                />

                                <RecordsPanel<Address>
                                    typeName="Address"
                                    clearanceSide={Side.Top}
                                    records={
                                        this.state.editing?.addresses ?? []
                                    }
                                    editor={record => (
                                        <AddressEditor
                                            value={record}
                                            onChange={updated =>
                                                this.updateEditAddress(
                                                    record,
                                                    updated,
                                                )
                                            }
                                        />
                                    )}
                                    onAdd={this.addAddress.bind(this)}
                                    onDelete={record =>
                                        this.deleteAddress(record)
                                    }
                                />

                                <Panel
                                    title="Status"
                                    bufferSide={Side.All}
                                    clearanceSide={Side.Top}>
                                    <InputForm
                                        binding={this.state.editing}
                                        display={RenderOrientation.Vertical}
                                        fieldDisplay={
                                            RenderOrientation.Horizontal
                                        }
                                        onChange={value =>
                                            this.setState(() => ({
                                                editing: value,
                                            }))
                                        }>
                                        <InputFormSubform
                                            display={
                                                RenderOrientation.Horizontal
                                            }
                                            fieldDisplay={
                                                RenderOrientation.Horizontal
                                            }>
                                            <InputFormField
                                                label="Student"
                                                field="isStudent"
                                                reverse={true}
                                                weight={20}
                                                type={InputFieldTypes.Check}
                                            />
                                            <InputFormField
                                                label="Code"
                                                field="studentCode"
                                                weight={80}
                                                dependsOn="isStudent"
                                            />
                                        </InputFormSubform>

                                        <InputFormSubform
                                            display={
                                                RenderOrientation.Horizontal
                                            }
                                            fieldDisplay={
                                                RenderOrientation.Horizontal
                                            }>
                                            <InputFormField
                                                label="Teacher"
                                                field="isTeacher"
                                                reverse={true}
                                                weight={20}
                                                type={InputFieldTypes.Check}
                                            />
                                            <InputFormField
                                                label="Code"
                                                field="teacherCode"
                                                weight={80}
                                                dependsOn="isTeacher"
                                            />
                                        </InputFormSubform>
                                        <InputFormField
                                            label="Online"
                                            field="active"
                                            reverse={true}
                                        />
                                    </InputForm>
                                </Panel>

                                <InputFormMandatory />
                            </WizardStep>
                            <WizardStep
                                step={1}
                                frameless={true}>
                                <Grid<InputUserRole>
                                    keyField="id"
                                    fullWidth={true}
                                    fullHeight={true}
                                    value={this.state.userRoles}
                                    valueEdited={this.rolesChanged.bind(this)}>
                                    <GridColumn
                                        header=""
                                        field="checked"
                                        editableField="available"
                                    />
                                    <GridColumn
                                        header="User Roles"
                                        field="name"
                                    />
                                    <GridColumn
                                        header=""
                                        icon="info"
                                        cellTooltipField="details"
                                    />
                                </Grid>
                            </WizardStep>
                        </Wizard>
                    </DialogContent>
                    <DialogFooter full={true}>
                        {this.state.editStep > 0 && (
                            <DialogCancel
                                text="Previous"
                                onClick={this.prevEdit.bind(this)}
                            />
                        )}
                        {this.state.editing?.id > 0 && (
                            <DialogCancel
                                icon="mode_off_on"
                                text="Activation"
                                onClick={this.activation.bind(this)}
                            />
                        )}
                        <Spacer />
                        <DialogCancel onClick={this.cancelEdit.bind(this)} />
                        <DialogOK
                            text={
                                this.state.editStep < 1 &&
                                this.state.editing?.id === 0
                                    ? "Next"
                                    : "Save and Close"
                            }
                            onClick={this.nextEdit.bind(this)}
                        />
                    </DialogFooter>
                </Dialog>

                <Dialog
                    showDialog={this.state.schoolsOpen}
                    showLoading={this.state.schoolsLoad}
                    dialogIcon="location_city"
                    dialogTitle="Schools"
                    canResize={false}
                    width={850}>
                    <DialogContent>
                        {this.state.selections.length > 1 && (
                            <Panel
                                bufferSide={Side.All}
                                clearanceSide={Side.Bottom}
                                className="error">
                                Editing multiple users - current assignments not
                                shown
                            </Panel>
                        )}

                        <Grid<InputUserSchool>
                            keyField="id"
                            fullWidth={true}
                            fullHeight={true}
                            value={this.state.userSchools}
                            valueEdited={this.schoolChanged.bind(this)}>
                            <GridColumn
                                header=""
                                field="checked"
                                editable={true}
                                width="40px"
                            />
                            <GridColumn
                                header="School"
                                field="name"
                                width="40%"
                            />
                            <GridColumn
                                header="Subtitle"
                                field="subtitle"
                                width="60%"
                            />
                        </Grid>
                    </DialogContent>
                    <DialogFooter>
                        <DialogCancel onClick={this.schoolsCancel.bind(this)} />
                        <DialogOK
                            text="Save and Close"
                            onClick={this.schoolsSave.bind(this)}
                        />
                    </DialogFooter>
                </Dialog>

                <Dialog
                    showDialog={this.state.rolesOpen}
                    showLoading={this.state.rolesLoad}
                    dialogIcon="assignment_ind"
                    dialogTitle="Roles"
                    canResize={false}
                    width={420}>
                    <DialogContent>
                        {this.state.selections.length > 1 && (
                            <Panel
                                bufferSide={Side.All}
                                clearanceSide={Side.Bottom}
                                className="error">
                                Editing multiple users - current assignments not
                                shown
                            </Panel>
                        )}

                        <Grid<InputUserRole>
                            keyField="id"
                            fullWidth={true}
                            fullHeight={true}
                            value={this.state.userRoles}
                            valueEdited={this.rolesChanged.bind(this)}>
                            <GridColumn
                                header=""
                                field="checked"
                                editableField="available"
                            />
                            <GridColumn
                                header="User Roles"
                                field="name"
                            />
                            <GridColumn
                                header=""
                                icon="info"
                                cellTooltipField="details"
                            />
                        </Grid>
                    </DialogContent>
                    <DialogFooter>
                        <DialogCancel onClick={this.rolesCancel.bind(this)} />
                        <DialogOK
                            text="Save and Close"
                            onClick={this.rolesSave.bind(this)}
                        />
                    </DialogFooter>
                </Dialog>

                <Dialog
                    showDialog={this.state.importOpen}
                    showLoading={this.state.importLoad}
                    dialogIcon="system_update_alt"
                    dialogTitle="Import"
                    canResize={false}
                    width={this.state.importWidth}>
                    <DialogContent>
                        <ConnectedImportWizard
                            importType={this.state.importType}
                            importStep={this.state.importStep}
                            setType={this.importSetType.bind(this)}
                            setStep={this.importSetStep.bind(this)}
                            validChanged={this.importProceedChanged.bind(this)}
                            setLoading={value =>
                                this.setState({ importLoad: value })
                            }
                        />
                    </DialogContent>
                    <DialogFooter full={true}>
                        {this.state.importStep > 0 &&
                            this.state.importStep < 2 && (
                                <DialogCancel
                                    text="Back"
                                    onClick={this.importLastStep.bind(this)}
                                />
                            )}
                        <Spacer />
                        {this.state.importStep !== 2 && (
                            <DialogCancel
                                text={
                                    this.state.importStep < 3
                                        ? "Cancel"
                                        : "Close"
                                }
                                onClick={this.importCancel.bind(this)}
                            />
                        )}
                        {this.state.importStep < 2 && (
                            <DialogOK
                                text="Next"
                                disabled={!this.state.importValid}
                                onClick={this.importNextStep.bind(this)}
                            />
                        )}
                    </DialogFooter>
                </Dialog>
            </section>
        );
    }
}

const mapStateToProps = (state: IAppState) => {
    const { user, userRoles, school, clientSystem } = state.db;
    return {
        users: user.allUsers,
        userRoles: userRoles.userRoles,
        schools: school.schools,
        passwordConfig: user.passwordConfig,
        customApps: clientSystem.clientSystems.some(cs => !cs.standard),
    };
};

const mapDispatchToProps = (dispatch: AppDispatch) => {
    return {
        setPageLoading: (loading: boolean) => dispatch(setPageLoading(loading)),
        initialiseAllUserDetails: () =>
            unwrappedDispatch(initialiseAllUserDetails()),
        initialiseUserPasswordDetails: () =>
            unwrappedDispatch(initialiseUserPasswordDetails()),
        initialiseUserRolesDetails: () =>
            unwrappedDispatch(initialiseUserRolesDetails()),
        initialiseSchoolDetails: () =>
            unwrappedDispatch(initialiseSchoolDetails()),
        updateAllUser: (details: User[]) =>
            unwrappedDispatch(updateAllUser(details)),
        deleteUsers: (users: User[]) => unwrappedDispatch(deleteUsers(users)),
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(RouteUsers);

interface ImportWizardProps {
    users?: User[];
    userRoles?: UserRole[];

    importType?: string;
    importStep: number;

    addAllUser: (details: User[]) => Promise<void>;
    updateAllUser: (details: User[]) => Promise<void>;

    setType?: (type: string) => void;
    setStep?: (step: number) => void;
    setLoading?: (loading: boolean) => void;
    validChanged?: (valid: boolean) => void;
}

interface ImportWizardState {
    importData?: DataSet;
    importFile?: File;
    importOptions?: User_ImportOptions;
    importLogs?: ImportLog;
    azMappings: AzImportMapping;
    azSettings?: AzImportSettings;

    azureADProperties: Array<string>;
    azureADUserGroups: Array<UserGroup>;
    azureADOrgName: string;
}

class ImportWizard extends React.Component<
    ImportWizardProps,
    ImportWizardState
> {
    state = {
        azMappings: {
            user: {},
            teacher: {},
            student: {},
            importUsers: false,
            importTeachers: false,
            importStudents: false,
            isAuthenticated: false,
        },
    } as ImportWizardState;

    private importTypes = [
        { value: "csv", text: "XLSX/CSV", order: 1 },
        { value: "tdf", text: "TD File", order: 2 },
        { value: "azr", text: "Microsoft Entra ID", order: 3 },
    ];

    private importConfig: SpreadsheetConfiguration = {
        Name: "Users",
        Tables: [
            {
                Name: "Users",
                Columns: [
                    { Field: "title", Name: "Title" },
                    { Field: "firstName", Name: "First Name" },
                    { Field: "middleName", Name: "Middle Name" },
                    { Field: "lastName", Name: "Family Name" },
                    { Field: "roles", Name: "Role" },
                    { PK: true, Field: "username", Name: "Email Address" },
                ],
            },
            {
                Name: "Teachers",
                Columns: [
                    { Field: "teacherCode", Name: "Code" },
                    { Field: "title", Name: "Title" },
                    { Field: "firstName", Name: "First Name" },
                    { Field: "middleName", Name: "Middle Name" },
                    { Field: "lastName", Name: "Family Name" },
                    { Field: "roles", Name: "Role" },
                    { PK: true, Field: "username", Name: "Email Address" },
                ],
            },
            {
                Name: "Students",
                Columns: [
                    { Field: "studentCode", Name: "Code" },
                    { Field: "title", Name: "Title" },
                    { Field: "firstName", Name: "First Name" },
                    { Field: "middleName", Name: "Middle Name" },
                    { Field: "lastName", Name: "Family Name" },
                    { Field: "roles", Name: "Role" },
                    { PK: true, Field: "username", Name: "Email Address" },
                ],
            },
        ],
    };

    componentDidMount(): void {
        this.setState({
            importData: undefined,
        });
    }

    componentDidUpdate(
        prevProps: Readonly<ImportWizardProps>,
        prevState: Readonly<ImportWizardState>,
        snapshot?: any,
    ): void {
        if (prevProps.importType !== this.props.importType) {
            this.setState({
                importData: undefined,
                importFile: undefined,
                importOptions: undefined,
            });

            this.props.validChanged?.(false);
        }

        if (prevProps.importStep !== this.props.importStep) {
            this.props.validChanged?.(this.importCanProceed());

            this.importProceed();
        } else if (prevState !== this.state) {
            this.props.validChanged?.(this.importCanProceed());
        }

        if (prevProps.importType !== "azr" && this.props.importType === "azr") {
            this.loadAzureSettings();
        }
    }

    async loadAzureSettings() {
        const settings = await Datastore.getType<AzImportSettings>(
            "api/Azure/ADApp/Settings",
        );

        this.setState({
            azSettings: settings,
        });
    }

    async azureLogin() {
        const location = {
            top:
                window.screenY + (window.outerHeight - window.innerHeight) + 10,
            left: window.screenX + window.outerWidth / 2 - 275,
        };
        const msLoginWindow = window.open(
            "about:blank",
            "msLoginWindow",
            "width=550,height=750,location=no,menubar=no,status=no,titlebar=no,resizable=no,top=" +
                location.top +
                ",left=" +
                location.left,
        );

        const commonOidConfig = await WebRequest.getType<any>(
            "https://login.microsoftonline.com/common/v2.0/.well-known/openid-configuration",
        );

        const authEndpoint = commonOidConfig.authorization_endpoint;
        const loc = document.location;
        const params = {
            client_id: this.state.azSettings.clientId,
            response_type: "code",
            redirect_uri: `${loc.protocol}//${loc.host}/OidHelper.html`,
            response_mode: "query",
            state: `${loc.protocol}//${loc.host}`,
            scope: "openid https://graph.microsoft.com/User.Read https://graph.microsoft.com/User.ReadBasic.All https://graph.microsoft.com/profile https://graph.microsoft.com/Group.Read.All",
            prompt: "consent",
        };

        const query = Object.keys(params)
            .map(
                k =>
                    encodeURIComponent(k) + "=" + encodeURIComponent(params[k]),
            )
            .join("&");

        msLoginWindow.location.href = authEndpoint + "?" + query;

        let tenantInfo: AzADTenantInfo = null;
        this.props.setLoading?.(true);

        window["azureInfoReceiver"] = async (data: AzADTenantInfo) => {
            if (!!data) {
                delete window["azureInfoReceiver"];
                tenantInfo = data;
                msLoginWindow.close();
            }
        };

        await new Promise(resolve => {
            let pollLoginWindow = window.setInterval(function () {
                try {
                    if (msLoginWindow.closed !== false) {
                        window.clearInterval(pollLoginWindow);
                        resolve(null);
                    }
                } catch (e) {} // eslint-disable-line no-empty
            }, 500);
        });

        if (!!tenantInfo) {
            const commonOidAuth = await Datastore.postType<AzAuthResponse>(
                "api/Azure/ADApp/Authenticate",
                {
                    AuthCode: tenantInfo.code,
                    RedirectUri: `${tenantInfo.state}/OidHelper.html`,
                    Scope: "openid https://graph.microsoft.com/User.Read",
                },
            );

            this.setState(prev => ({
                azMappings: {
                    ...prev.azMappings,
                    accessToken: commonOidAuth.accessToken,
                    isAuthenticated: true,
                },
            }));

            await this.loadAzureAD(commonOidAuth.accessToken);
        }

        this.props.setLoading?.(false);
        this.props.validChanged?.(this.importCanProceed());
    }

    async loadAzureAD(token?: string) {
        this.props.setLoading?.(true);

        token = token ?? this.state.azMappings.accessToken;
        const pResult = await Datastore.getType<AzImportSettings>(
            `api/Azure/${token}/ADApp/Profile`,
        );

        if (!!pResult.errorMessage) {
            addAlert({
                icon: "error",
                styleColor: "red",
                title: "Error",
                text: pResult.errorMessage,
            });

            this.props.setLoading?.(false);
            return false;
        }
        this.setState({
            azureADProperties: pResult.properties,
        });

        const gResult = await Datastore.getType<AzImportSettings>(
            `api/Azure/${token}/ADApp/UserGroups`,
        );

        if (!!gResult.errorMessage) {
            addAlert({
                icon: "error",
                styleColor: "red",
                title: "Error",
                text: gResult.errorMessage,
            });

            this.props.setLoading?.(false);
            return false;
        }

        this.setState({
            azureADUserGroups: gResult.groups,
            azureADOrgName: gResult.organisationName,
        });

        this.props.setLoading?.(false);
    }

    importCanProceed(): boolean {
        switch (this.props.importStep) {
            case 0:
                switch (this.props.importType) {
                    case "csv":
                        return this.state.importData !== undefined;
                    case "tdf":
                        return this.state.importFile !== undefined;
                    case "azr":
                        return (
                            !!this.state.azureADUserGroups &&
                            !!this.state.azureADOrgName
                        );
                    default:
                        return false;
                }
            case 1:
                switch (this.props.importType) {
                    case "tdf":
                        return (
                            this.state.importOptions &&
                            (this.state.importOptions.importStudents ||
                                this.state.importOptions.importTeachers ||
                                this.state.importOptions.removeItemsNotInFile)
                        );
                    case "azr":
                        return (
                            this.state.azMappings &&
                            (this.state.azMappings.importUsers ||
                                this.state.azMappings.importStudents ||
                                this.state.azMappings.importTeachers) &&
                            (!this.state.azMappings.importUsers ||
                                !!this.state.azMappings.user.userGroupId) &&
                            (!this.state.azMappings.importTeachers ||
                                (!!this.state.azMappings.teacher.code &&
                                    !!this.state.azMappings.teacher
                                        .userGroupId)) &&
                            (!this.state.azMappings.importStudents ||
                                (!!this.state.azMappings.student.code &&
                                    !!this.state.azMappings.student
                                        .userGroupId)) &&
                            !!this.state.azMappings.firstName &&
                            !!this.state.azMappings.lastName &&
                            !!this.state.azMappings.emailAddress
                        );
                    default:
                        return false;
                }
            case 3:
                return true;
            default:
                return false;
        }
    }

    importProceed() {
        switch (this.props.importStep) {
            case 1:
                switch (this.props.importType) {
                    case "csv":
                        this.doCsvImport();
                        break;
                    case "tdf":
                        this.setState({
                            importOptions: {
                                importTeachers: false,
                                importStudents: false,
                                removeItemsNotInFile: false,
                            },
                        });
                        break;
                    case "azr":
                        break;
                }
                break;
            case 2:
                switch (this.props.importType) {
                    case "tdf":
                        this.doTDImport();
                        break;
                    case "azr":
                        this.doAzrImport();
                        break;
                }
                break;
        }
    }

    async doCsvImport() {
        try {
            this.props.setStep?.(2);

            const adds = [];
            const upd = [];
            const logs: ImportLog = {
                upsertCounts: [],
                skippedItems: [],
            };

            for (const config of this.importConfig.Tables) {
                const table = this.state.importData[config.Name];
                if (!table) continue;

                this.processCsvImport(config, table, adds, upd, logs);
            }

            const waits: Promise<void>[] = [];
            if (adds.length > 0) waits.push(this.props.addAllUser(adds));
            if (upd.length > 0) waits.push(this.props.updateAllUser(upd));
            await Promise.all(waits);

            this.setState({
                importLogs: logs,
            });

            this.props.setStep?.(3);
        } catch {
            this.props.setStep?.(1);
        }
    }

    processCsvImport(
        type: SpreadsheetTableDefinition,
        data: DataTable,
        adds: User[],
        upd: User[],
        logs: ImportLog,
    ): void {
        for (const row of data) {
            const pk = type.Columns.find(c => c.PK)?.Field;
            if (!pk) continue;

            const pkVal = row[pk]?.toString().trim();
            if (!pkVal) continue;

            let user = this.props.users?.find(
                u => u[pk]?.toLowerCase() === pkVal.toLowerCase(),
            );

            if (!user) {
                user = new User({
                    [pk]: pkVal,
                });

                adds.push(user);
            } else {
                if (!upd.includes(user)) upd.push(user);
            }

            for (const column of type.Columns) {
                if (column.PK) continue;

                const value = row[column.Field]?.toString().trim();
                if (!value) continue;

                switch (column.Field) {
                    case "roles":
                        const roleNames = value.split(",").map(r => r.trim());
                        user.roles = (
                            this.props.userRoles
                                ?.filter(ur => roleNames.includes(ur.name))
                                .map(ur => ur.name) ?? []
                        ).concat(["Users"]);
                        break;
                    default:
                        Reflect.set(user, column.Field as keyof User, value);
                        break;
                }
            }
        }

        logs.upsertCounts.push({
            name: type.Name,
            insertCount: adds.length,
            updateCount: upd.length,
            archiveCount: 0,
        });
    }

    async doTDImport() {
        try {
            this.props.setStep?.(2);

            const logs = await importUsersFromTD(
                this.state.importFile.id,
                this.state.importOptions,
            );

            this.setState({
                importLogs: logs,
            });

            this.props.setStep?.(3);
        } catch {
            this.props.setStep?.(1);
        }
    }

    async doAzrImport() {
        try {
            this.props.setStep?.(2);

            const logs = await importUsersFromAzr(this.state.azMappings);

            this.setState({
                importLogs: logs,
            });

            this.props.setStep?.(3);
        } catch {
            this.props.setStep?.(1);
        }
    }

    render() {
        return (
            <>
                <SelectList
                    tabs={true}
                    value={this.props.importType}
                    options={this.importTypes}
                    onChange={value =>
                        this.props.importStep === 0 &&
                        this.props.setType?.(value)
                    }
                />

                <Wizard
                    flow={true}
                    frameless={true}
                    transparent={true}
                    clearanceSide={Side.Top}
                    step={this.props.importStep}>
                    <WizardStep step={0}>
                        {(() => {
                            switch (this.props.importType) {
                                case "csv":
                                    return (
                                        <Import
                                            config={this.importConfig}
                                            template={template}
                                            templateName={template.stripReactName()}
                                            onReady={(_, data) =>
                                                this.setState({
                                                    importData: data,
                                                })
                                            }
                                        />
                                    );
                                case "tdf":
                                    return (
                                        <FileExplorer
                                            type={FileExplorerTypes.Timetables}
                                            value={this.state.importFile}
                                            onChange={item =>
                                                this.setState({
                                                    importFile: item,
                                                })
                                            }
                                        />
                                    );
                                case "azr":
                                    return (
                                        <>
                                            <button
                                                id="azureConntectButton"
                                                className="actionbutton column full theme-button"
                                                style={{ height: "40px" }}
                                                disabled={
                                                    this.state.azSettings ===
                                                    null
                                                }
                                                onClick={this.azureLogin.bind(
                                                    this,
                                                )}>
                                                <img
                                                    src="data:image/svg+xml;base64,PD94bWwgdmVyc2lvbj0iMS4wIiBlbmNvZGluZz0iVVRGLTgiPz4KPHN2ZyBpZD0idXVpZC1mOGQ0ZDM5Mi03YzEyLTRiZDktYmFmZi02NmZiZjc4MTRiOTEiIGRhdGEtbmFtZT0iTGF5ZXIgMSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIiB3aWR0aD0iMTgiIGhlaWdodD0iMTgiIHZpZXdCb3g9IjAgMCAxOCAxOCI+CiAgPHBhdGggZD0ibTMuODAyLDE0LjAzMmMuMzg4LjI0MiwxLjAzMy41MTEsMS43MTUuNTExLjYyMSwwLDEuMTk4LS4xOCwxLjY3Ni0uNDg3LDAsMCwuMDAxLDAsLjAwMi0uMDAxbDEuODA1LTEuMTI4djQuMDczYy0uMjg2LDAtLjU3NC0uMDc4LS44MjQtLjIzNGwtNC4zNzQtMi43MzRaIiBmaWxsPSIjMjI1MDg2Ii8+CiAgPHBhdGggZD0ibTcuODUzLDEuNTA3TC4zNTMsOS45NjdjLS41NzkuNjU0LS40MjgsMS42NDIuMzIzLDIuMTExLDAsMCwyLjc3NiwxLjczNSwzLjEyNiwxLjk1NC4zODguMjQyLDEuMDMzLjUxMSwxLjcxNS41MTEuNjIxLDAsMS4xOTgtLjE4LDEuNjc2LS40ODcsMCwwLC4wMDEsMCwuMDAyLS4wMDFsMS44MDUtMS4xMjgtNC4zNjQtMi43MjgsNC4zNjUtNC45MjRWMXMwLDAsMCwwYy0uNDI0LDAtLjg0Ny4xNjktMS4xNDcuNTA3WiIgZmlsbD0iIzZkZiIvPgogIDxwb2x5Z29uIHBvaW50cz0iNC42MzYgMTAuMTk5IDQuNjg4IDEwLjIzMSA5IDEyLjkyNyA5LjAwMSAxMi45MjcgOS4wMDEgMTIuOTI3IDkuMDAxIDUuMjc2IDkgNS4yNzUgNC42MzYgMTAuMTk5IiBmaWxsPSIjY2JmOGZmIi8+CiAgPHBhdGggZD0ibTE3LjMyNCwxMi4wNzhjLjc1MS0uNDY5LjkwMi0xLjQ1Ny4zMjMtMi4xMTFsLTQuOTIxLTUuNTUxYy0uMzk3LS4xODUtLjg0Mi0uMjkxLTEuMzEzLS4yOTEtLjkyNSwwLTEuNzUyLjM5OS0yLjMwMiwxLjAyNmwtLjEwOS4xMjNoMHM0LjM2NCw0LjkyNCw0LjM2NCw0LjkyNGgwczAsMCwwLDBsLTQuMzY1LDIuNzI4djQuMDczYy4yODcsMCwuNTczLS4wNzguODIzLS4yMzRsNy41LTQuNjg4WiIgZmlsbD0iIzA3NDc5MyIvPgogIDxwYXRoIGQ9Im05LjAwMSwxdjQuMjc1cy4xMDktLjEyMy4xMDktLjEyM2MuNTUtLjYyNywxLjM3Ny0xLjAyNiwyLjMwMi0xLjAyNi40NzIsMCwuOTE2LjEwNywxLjMxMy4yOTFsLTIuNTc5LTIuOTA5Yy0uMjk5LS4zMzgtLjcyMy0uNTA3LTEuMTQ2LS41MDdaIiBmaWxsPSIjMDI5NGU0Ii8+CiAgPHBvbHlnb24gcG9pbnRzPSIxMy4zNjUgMTAuMTk5IDEzLjM2NSAxMC4xOTkgMTMuMzY1IDEwLjE5OSA5LjAwMSA1LjI3NiA5LjAwMSAxMi45MjYgMTMuMzY1IDEwLjE5OSIgZmlsbD0iIzk2YmNjMiIvPgo8L3N2Zz4="
                                                    style={{
                                                        width: 20,
                                                        height: 20,
                                                        top: 0,
                                                    }}
                                                    alt="Microsoft Entra ID Icon"
                                                />
                                                <span>
                                                    Connect to Microsoft Entra
                                                    ID
                                                </span>
                                            </button>
                                        </>
                                    );
                                default:
                                    return null;
                            }
                        })()}
                    </WizardStep>
                    <WizardStep
                        step={1}
                        framed={this.props.importType === "tdf"}>
                        {(() => {
                            switch (this.props.importType) {
                                case "tdf":
                                    return this.state.importOptions ? (
                                        <>
                                            <Info>
                                                Please select options for import
                                                of data:
                                            </Info>

                                            <GeneralContainer
                                                clearanceSide={Side.Top}>
                                                <Checkbox
                                                    label="Import Teachers"
                                                    value={
                                                        this.state.importOptions
                                                            .importTeachers
                                                    }
                                                    onChange={value =>
                                                        this.setState({
                                                            importOptions: {
                                                                ...this.state
                                                                    .importOptions,
                                                                importTeachers:
                                                                    value,
                                                            },
                                                        })
                                                    }
                                                />
                                                <Checkbox
                                                    label="Import Students"
                                                    value={
                                                        this.state.importOptions
                                                            .importStudents
                                                    }
                                                    onChange={value =>
                                                        this.setState({
                                                            importOptions: {
                                                                ...this.state
                                                                    .importOptions,
                                                                importStudents:
                                                                    value,
                                                            },
                                                        })
                                                    }
                                                />
                                                <Checkbox
                                                    label="Remove Teachers or Students not in file"
                                                    value={
                                                        this.state.importOptions
                                                            .removeItemsNotInFile
                                                    }
                                                    onChange={value =>
                                                        this.setState({
                                                            importOptions: {
                                                                ...this.state
                                                                    .importOptions,
                                                                removeItemsNotInFile:
                                                                    value,
                                                            },
                                                        })
                                                    }
                                                />
                                            </GeneralContainer>
                                        </>
                                    ) : null;
                                case "azr":
                                    return this.state.azMappings ? (
                                        <ADImportSettings
                                            azMappings={this.state.azMappings}
                                            azureADProperties={
                                                this.state.azureADProperties
                                            }
                                            azureADUserGroups={
                                                this.state.azureADUserGroups
                                            }
                                            onChange={azMappings =>
                                                this.setState({ azMappings })
                                            }
                                        />
                                    ) : null;
                            }
                        })()}
                    </WizardStep>
                    <WizardStep
                        step={2}
                        framed={true}
                        hiddenWhenOffscreen={true}>
                        <div className="buffer text-center">
                            <h2>We're currently importing your data</h2>
                        </div>
                        <div className="process-indicator">
                            <div className="cube cube1"></div>
                            <div className="cube cube2"></div>
                            <div className="cube cube3"></div>
                            <div className="cube cube4"></div>
                            <div className="cube cube5"></div>
                            <div className="cube cube6"></div>
                            <div className="cube cube7"></div>
                            <div className="cube cube8"></div>
                            <div className="cube cube9"></div>
                            <div className="cube cube10"></div>
                            <div className="cube cube11"></div>
                            <div className="document1"></div>
                            <div className="document2"></div>
                        </div>
                    </WizardStep>
                    <WizardStep
                        step={3}
                        hiddenWhenOffscreen={true}>
                        <Panel>
                            <Panel
                                title="Addition/Update Counts"
                                bufferSide={Side.All}>
                                {this.state?.importLogs?.upsertCounts?.length >
                                0 ? (
                                    <Grid<UpsertCount>
                                        value={
                                            this.state.importLogs
                                                ?.upsertCounts ?? []
                                        }
                                        keyField="name"
                                        fullWidth={true}>
                                        <GridColumn
                                            header="Type"
                                            field="name"
                                        />
                                        <GridColumn
                                            header="Added"
                                            field="insertCount"
                                        />
                                        <GridColumn
                                            header="Updated"
                                            field="updateCount"
                                        />
                                    </Grid>
                                ) : (
                                    <Info>
                                        No records were added or updated.
                                    </Info>
                                )}
                            </Panel>

                            <Panel
                                title="Skipped Items"
                                bufferSide={Side.All}
                                clearanceSide={Side.Top}>
                                {this.state?.importLogs?.skippedItems?.length >
                                0 ? (
                                    <Grid<SkippedRecordCount>
                                        value={
                                            this.state.importLogs
                                                ?.skippedItems ?? []
                                        }
                                        keyField="name"
                                        fullWidth={true}>
                                        <GridColumn
                                            header="Type"
                                            field="name"
                                        />
                                        <GridColumn
                                            header="Number"
                                            field="count"
                                        />
                                        <GridColumn
                                            header="Reason"
                                            field="reason"
                                        />
                                    </Grid>
                                ) : (
                                    <Info>No records were skipped.</Info>
                                )}
                            </Panel>
                        </Panel>
                    </WizardStep>
                </Wizard>
            </>
        );
    }
}

const mapStateToPropsWizard = (state: IAppState) => {
    const { user, userRoles } = state.db;
    return {
        users: user.allUsers,
        userRoles: userRoles.userRoles,
    };
};

const mapDispatchToPropsWizard = (dispatch: AppDispatch) => {
    return {
        addAllUser: (details: User[]) => unwrappedDispatch(addAllUser(details)),
        updateAllUser: (details: User[]) =>
            unwrappedDispatch(updateAllUser(details)),
    };
};

const ConnectedImportWizard = connect(
    mapStateToPropsWizard,
    mapDispatchToPropsWizard,
)(ImportWizard);

interface ADImportSettingsProps {
    azMappings?: AzImportMapping;
    azureADProperties: Array<string>;
    azureADUserGroups: Array<UserGroup>;

    onChange?: (azMappings: AzImportMapping) => void;
}

class ADImportSettings extends React.Component<ADImportSettingsProps> {
    render() {
        return (
            <>
                <GeneralContainer
                    clearanceSide={Side.Top}
                    flex={FlexDirection.Row}
                    flexSpaced={true}>
                    <Checkbox
                        label="Import Users"
                        value={this.props.azMappings.importUsers}
                        className="button col textCenter"
                        onChange={value =>
                            this.props.onChange?.({
                                ...this.props.azMappings,
                                importUsers: value,
                            })
                        }
                    />
                    <Checkbox
                        label="Import Teachers"
                        value={this.props.azMappings.importTeachers}
                        className="button col textCenter"
                        onChange={value =>
                            this.props.onChange?.({
                                ...this.props.azMappings,
                                importTeachers: value,
                            })
                        }
                    />
                    <Checkbox
                        label="Import Students"
                        value={this.props.azMappings.importStudents}
                        className="button col textCenter"
                        onChange={value =>
                            this.props.onChange?.({
                                ...this.props.azMappings,
                                importStudents: value,
                            })
                        }
                    />
                </GeneralContainer>

                {(this.props.azMappings.importUsers ||
                    this.props.azMappings.importTeachers ||
                    this.props.azMappings.importStudents) && (
                    <GeneralContainer
                        clearanceSide={Side.Top}
                        flex={FlexDirection.Column}>
                        {this.props.azMappings.importUsers && (
                            <Panel
                                flex={FlexDirection.Column}
                                clearanceSide={Side.Top}
                                clearanceSize={Size.Large}
                                bufferSide={Side.All}>
                                <h3>User Configuration</h3>

                                <InputForm
                                    binding={this.props.azMappings.user}
                                    display={RenderOrientation.Vertical}
                                    fieldDisplay={RenderOrientation.Horizontal}
                                    clearanceSide={Side.Top}>
                                    <InputFormField
                                        label="User Group"
                                        field="userGroupId"
                                        type={InputFieldTypes.Select}
                                        options={this.props.azureADUserGroups.map(
                                            g => ({
                                                value: g.id,
                                                text: g.displayName,
                                            }),
                                        )}
                                        onChange={value =>
                                            this.props.onChange({
                                                ...this.props.azMappings,
                                                user: {
                                                    ...this.props.azMappings
                                                        .user,
                                                    userGroupId: value,
                                                },
                                            })
                                        }
                                    />
                                </InputForm>
                            </Panel>
                        )}

                        {this.props.azMappings.importTeachers && (
                            <Panel
                                flex={FlexDirection.Column}
                                clearanceSide={Side.Top}
                                clearanceSize={Size.Large}
                                bufferSide={Side.All}>
                                <h3>Teacher Configuration</h3>

                                <InputForm
                                    binding={this.props.azMappings.teacher}
                                    display={RenderOrientation.Vertical}
                                    fieldDisplay={RenderOrientation.Horizontal}
                                    clearanceSide={Side.Top}>
                                    <InputFormField
                                        label="User Group"
                                        field="userGroupId"
                                        type={InputFieldTypes.Select}
                                        options={this.props.azureADUserGroups.map(
                                            g => ({
                                                value: g.id,
                                                text: g.displayName,
                                            }),
                                        )}
                                        onChange={value =>
                                            this.props.onChange({
                                                ...this.props.azMappings,
                                                user: {
                                                    ...this.props.azMappings
                                                        .user,
                                                    userGroupId: value,
                                                },
                                            })
                                        }
                                    />
                                    <InputFormField
                                        label="Teacher Code"
                                        field="teacherCode"
                                        type={InputFieldTypes.Select}
                                        options={this.props.azureADProperties.map(
                                            g => ({
                                                value: g,
                                                text: g,
                                            }),
                                        )}
                                        onChange={value =>
                                            this.props.onChange({
                                                ...this.props.azMappings,
                                                teacher: {
                                                    ...this.props.azMappings
                                                        .teacher,
                                                    code: value,
                                                },
                                            })
                                        }
                                    />
                                </InputForm>
                            </Panel>
                        )}

                        {this.props.azMappings.importStudents && (
                            <Panel
                                flex={FlexDirection.Column}
                                clearanceSide={Side.Top}
                                clearanceSize={Size.Large}
                                bufferSide={Side.All}>
                                <h3>Student Configuration</h3>

                                <InputForm
                                    binding={this.props.azMappings.student}
                                    display={RenderOrientation.Vertical}
                                    fieldDisplay={RenderOrientation.Horizontal}
                                    clearanceSide={Side.Top}>
                                    <InputFormField
                                        label="User Group"
                                        field="userGroupId"
                                        type={InputFieldTypes.Select}
                                        options={this.props.azureADUserGroups.map(
                                            g => ({
                                                value: g.id,
                                                text: g.displayName,
                                            }),
                                        )}
                                        onChange={value =>
                                            this.props.onChange({
                                                ...this.props.azMappings,
                                                student: {
                                                    ...this.props.azMappings
                                                        .student,
                                                    userGroupId: value,
                                                },
                                            })
                                        }
                                    />
                                    <InputFormField
                                        label="Student Code"
                                        field="studentCode"
                                        type={InputFieldTypes.Select}
                                        options={this.props.azureADProperties.map(
                                            g => ({
                                                value: g,
                                                text: g,
                                            }),
                                        )}
                                        onChange={value =>
                                            this.props.onChange({
                                                ...this.props.azMappings,
                                                student: {
                                                    ...this.props.azMappings
                                                        .student,
                                                    code: value,
                                                },
                                            })
                                        }
                                    />
                                </InputForm>
                            </Panel>
                        )}

                        <Panel
                            flex={FlexDirection.Column}
                            clearanceSide={Side.Top}
                            clearanceSize={Size.Large}
                            bufferSide={Side.All}>
                            <h3>Common Configuration</h3>

                            <InputForm
                                binding={this.props.azMappings}
                                display={RenderOrientation.Vertical}
                                fieldDisplay={RenderOrientation.Horizontal}
                                clearanceSide={Side.Top}>
                                <InputFormField
                                    label="First Name"
                                    field="firstName"
                                    type={InputFieldTypes.Select}
                                    options={this.props.azureADProperties.map(
                                        g => ({
                                            value: g,
                                            text: g,
                                        }),
                                    )}
                                    onChange={value =>
                                        this.props.onChange({
                                            ...this.props.azMappings,
                                            firstName: value,
                                        })
                                    }
                                />
                                <InputFormField
                                    label="Last Name"
                                    field="lastName"
                                    type={InputFieldTypes.Select}
                                    options={this.props.azureADProperties.map(
                                        g => ({
                                            value: g,
                                            text: g,
                                        }),
                                    )}
                                    onChange={value =>
                                        this.props.onChange({
                                            ...this.props.azMappings,
                                            lastName: value,
                                        })
                                    }
                                />
                                <InputFormField
                                    label="Email Address"
                                    field="emailAddress"
                                    type={InputFieldTypes.Select}
                                    options={this.props.azureADProperties.map(
                                        g => ({
                                            value: g,
                                            text: g,
                                        }),
                                    )}
                                    onChange={value =>
                                        this.props.onChange({
                                            ...this.props.azMappings,
                                            emailAddress: value,
                                        })
                                    }
                                />
                            </InputForm>
                        </Panel>
                    </GeneralContainer>
                )}
            </>
        );
    }
}
